import React, { useState } from 'react';
import Styled from 'styled-components';
import { Headline } from '@amount/frontend-components';

import { ContentPaddingContainer, SettingsTableLayout } from '../../CommonStyles';
import { IDocumentUploadProps } from '../common';
import DropForCustomer from '../DropForCustomer';
import SessionManager from '../../../../util/sessionManager';
import NewWindowLink from '../../NewWindowLink';

const InfoContainer = Styled.div`
  display: flex;
  gap: 2rem;
  align-items: flex-start;

  @media (max-width: 968px) {
    flex-direction: column;
  }
`;

const MainContent = Styled.div`
  flex: 1;
  min-width: 0;
`;

const Sidebar = Styled.div`
  flex: 0 0 280px;
  position: sticky;
  top: 10rem;
  right: 2rem;

  @media (max-width: 968px) {
    position: static;
    width: 100%;
  }
`;

const InstructionsPanel = Styled.div`
  padding: 1.5rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
`;

const InstructionsList = Styled.ul`
  margin: 1rem 0;
  padding-left: 1.5rem;
  line-height: 1.6;

  li {
    margin-bottom: 0.75rem;
  }
`;

const FaqLinkWrapper = Styled.div`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e0e0e0;

  a {
    color: ${props => props.theme.brand.colorPrimary};
    text-decoration: none;
    font-weight: 500;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const SuccessContainer = Styled.div`
  padding: 2rem 0;
`;

export const DocumentUpload: React.FunctionComponent<IDocumentUploadProps> = ({
  customerUUID
}) => {
  const [uploadComplete, setUploadComplete] = useState(false);

  if (uploadComplete) {
    return (
      <SettingsTableLayout as='div'>
        <ContentPaddingContainer>
          <SuccessContainer>
            <Headline scale='large' as='h1'>Document Upload Successful</Headline>
          </SuccessContainer>
        </ContentPaddingContainer>
      </SettingsTableLayout>
    );
  }

  return (
    <InfoContainer>
      <MainContent>
        <SettingsTableLayout as='div'>
          <ContentPaddingContainer>
            <Headline scale='large' as='h1'>Secure Document Upload</Headline>
            <p>
              Quickly and securely upload the documents needed directly to your account,
              protecting your personal information from the risks of sending sensitive
              files via email.
            </p>

            <DropForCustomer
              customerUUID={customerUUID}
              uploadType='general'
              onSuccess={() => setUploadComplete(true)}
            />
          </ContentPaddingContainer>
        </SettingsTableLayout>
      </MainContent>

      <Sidebar>
        <InstructionsPanel>
          <Headline scale='medium' as='h3'>Uploading Your Documents</Headline>

          <InstructionsList>
            <li>Click the "Browse from Device" button to locate the document on your device.</li>
            <li>Review your selected file by clicking "View".</li>
            <li>When you are ready to upload, click "Submit for Review".</li>
            <li>
              A loading experience will appear while your document is being securely transferred.
              Do not close your browser during this time.
            </li>
            <li>Once your upload is complete, a confirmation message will appear on the screen.</li>
          </InstructionsList>

          <FaqLinkWrapper>
            <NewWindowLink href={SessionManager.urlRouter.faqUrl}>
              FAQs
            </NewWindowLink>
          </FaqLinkWrapper>
        </InstructionsPanel>
      </Sidebar>
    </InfoContainer>
  );
};
